/**
 * Datas puras (`YYYY-MM-DD`) — o dia, sem instante e sem fuso.
 *
 * Vencimento de título, previsão de entrega, dia de um ponto de gráfico: são
 * DIAS. Formatá-los com `new Date(iso).toLocaleDateString('pt-BR')` volta um
 * dia no Brasil inteiro, e volta em silêncio. `new Date('2026-09-14')` é
 * interpretado como meia-noite **UTC** (a spec manda: data pura é UTC, data
 * com hora é local), e o `toLocaleDateString` renderiza isso no fuso do
 * dispositivo — 21h de 13/09 em UTC-3. A OC dizia 13/09 para quem escolheu
 * 14/09 no calendário.
 *
 * Não existe "corrigir depois": somar três horas acerta no Brasil e erra em
 * qualquer outro lugar. O certo é nunca construir um instante a partir de algo
 * que não é um instante — por isso estas funções fatiam o texto.
 *
 * Para um `timestamp` de verdade (`createdAt`, `approvedAt`, quando algo
 * aconteceu) o `new Date(...).toLocaleDateString()` continua certo: ali existe
 * um instante, e exibi-lo no fuso de quem lê é o comportamento desejado.
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** `YYYY-MM-DD` → `DD/MM/AAAA`. Texto que não for data pura volta como veio. */
export function formatDateBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = ISO_DATE.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** `YYYY-MM-DD` → `DD/MM`. Para eixo de gráfico e lista densa. */
export function formatDayMonthBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = ISO_DATE.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}`;
}

/** Hoje (`YYYY-MM-DD`) no fuso informado — default o da oficina, que é o fuso
 *  em que a API decide o que está vencido. */
export function todayIsoIn(timeZone = 'America/Sao_Paulo'): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Compara duas datas puras sem construir `Date`. `a < b` → negativo. */
export function compareIsoDate(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Soma dias a uma data pura, devolvendo data pura. Usa UTC internamente de
 *  propósito: aritmética em UTC não tem horário de verão, e o resultado é
 *  reconvertido para texto antes de qualquer leitor ver um instante. */
export function addDaysIsoDate(iso: string, days: number): string {
  const m = ISO_DATE.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Todos os dias de `from` a `to`, inclusive, como datas puras.
 *
 *  É o que falta a uma série vinda de `GROUP BY dia`: o SQL devolve só os dias
 *  com linha, e um gráfico montado direto em cima disso desenha "o mês" com um
 *  ponto só. Intervalo invertido devolve vazio; o teto de 366 evita que um
 *  filtro maluco vire um laço infinito. */
export function eachDayIso(from: string, to: string, maxDays = 366): string[] {
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to) || from > to) return [];
  const days: string[] = [];
  let cursor = from.slice(0, 10);
  const end = to.slice(0, 10);
  while (cursor <= end && days.length < maxDays) {
    days.push(cursor);
    cursor = addDaysIsoDate(cursor, 1);
  }
  return days;
}
